import {AfterViewChecked, AfterViewInit, Component, ElementRef, Input, ViewChild} from '@angular/core';
import * as _ from 'lodash';
import {HelperService} from 'src/app/_services/helper.service';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'ui-datatable',
    templateUrl: './ui-data-table.component.html'
})
export class UIDataTableComponent implements AfterViewInit, AfterViewChecked {
    private _bordersV: boolean = true;
    private _bordersH: boolean = true;
    private _resizable: boolean = true;
    private _resizeStrategy: 'normal' | 'individual' = 'individual';
    private _outerBorders: boolean = false;
    private _stretch: boolean = false;
    private _itemSize: number;
    @ViewChild('table') table: ElementRef<HTMLTableElement>;
    debounceHandleMouseMove = _.throttle((event: MouseEvent, columnToResize: HTMLElement) => {

    }, 20);
    get bordersV() {
        return this._bordersV;
    }
    @Input() set bordersV(value: boolean) {
        this._bordersV = value;
        // this.setBorders();
    }
    get bordersH() {
        return this._bordersH;
    }
    @Input() set bordersH(value: boolean) {
        this._bordersH = value;
        // this.setBorders();
    }
    get resizable() {
        return this._resizable;
    }
    // Resize
    @Input() set resizable(value: boolean) {
        this._resizable = value;
    }
    get resizeStrategy() {
        return this._resizeStrategy;
    }
    @Input() set resizeStrategy(value: 'normal' | 'individual') {
        this._resizeStrategy = value;
    }
    get outerBorders(): boolean {
        return this._outerBorders;
    }
    // Border
    @Input() set outerBorders(value: boolean) {
        this._outerBorders = value;
        // this.setBorders();
    }
    get stretch(): boolean {
        return this._stretch;
    }
    // Stretch
    @Input() set stretch(value: boolean) {
        this._stretch = value;
    }

    // Item size
    @Input() set itemSize(value: number) {
        this._itemSize = value;
    }
    get itemSize() {
        return this._itemSize;
    }
    // Amount of cells per row
    get rowLength() {
        return this.table?.nativeElement?.firstElementChild?.children?.length ?? 0;
    }

    constructor(
        private host: ElementRef<HTMLElement>
    ) {}

    ngAfterViewInit(): void {
        this.host.nativeElement.classList.add('≈block', '≈w-full');
        this.validateForUnknownElements();
        this.removeNoneNativeElements();
        this.addDragBars();
        this.setBorders();
    }

    ngAfterViewChecked() {
        this.setColumnWidth();
    }

    validateForUnknownElements(): void {
        if (!this.table) {
            return;
        }

        const allChildren = Array.from(this.table.nativeElement.children) as HTMLElement[];
        const someChildIsNotUIDataRow = allChildren.some(child => !child.tagName || child.tagName !== 'UI-DATA-ROW');

        if (someChildIsNotUIDataRow) {

            allChildren.forEach(child => {
                if (!child.tagName || child.tagName !== 'UI-DATA-ROW') {
                    child.remove();
                }
            });

            throw new Error((`${this.constructor.name}: ui-datatable only supports ui-data-row elements`));
        }
    }

    removeNoneNativeElements(): void {
        if (!this.table) {
            return;
        }

        const allRows = Array.from(this.table.nativeElement.children).map(child => Array.from(child.children)).flat() as HTMLElement[];
        this.table.nativeElement.replaceChildren(...allRows);
    }


    addDragBars(): void {
        // if (!this.resizable || !this.table)
        //     return;

        const headers = Array.from(this.table.nativeElement.children[0].children) as HTMLElement[];
        let columnBeingResized: HTMLElement;
        document.addEventListener('mouseup', () => columnBeingResized = undefined);
        document.addEventListener('mousemove', (event) => {
            if (!columnBeingResized) {
                return;
            }

            const isTableBiggerThanParent = this.table.nativeElement.scrollWidth > this.table.nativeElement.offsetWidth;
            const isColSmallestPossible = event.movementX === 0
                || (event.movementX < 0 && event.clientX < columnBeingResized.getBoundingClientRect().left + 30)
                || (event.movementX >= 0 && event.clientX <= columnBeingResized.getBoundingClientRect().right);
            const isNextColSmallestPossible = event.movementX === 0
                || (event.movementX > 0 && event.clientX > columnBeingResized.nextElementSibling?.getBoundingClientRect().right - 30)
                || (event.movementX <= 0 && event.clientX >= (columnBeingResized.nextElementSibling as HTMLElement)?.getBoundingClientRect().left);


            if (isColSmallestPossible || isNextColSmallestPossible || isTableBiggerThanParent) {
                return;
            }

            const counterSizeNextHeader = () => {
                if (this.resizeStrategy === 'individual') {
                    const nextHeader = columnBeingResized?.nextElementSibling as HTMLElement;
                    if (!!nextHeader) {
                        nextHeader.style.width = `${nextHeader.getBoundingClientRect().right - event.clientX}px`;
                    }
                }
            };

            counterSizeNextHeader();
            columnBeingResized.style.width = `${event.clientX - columnBeingResized.getBoundingClientRect().left}px`;
        });

        const dragBar = (header: HTMLElement) => {
            const dragBarDomElement = document.createElement('drag-bar');
            dragBarDomElement.classList
                .add('≈border-r-2', '≈border-transparent', 'hover:≈border-gray-800', '≈absolute', '≈right-0', '≈top-0', '≈h-full',
                    '≈cursor-col-resize', '≈select-none', '≈z-20');
            dragBarDomElement.style.height = `${this.table.nativeElement.scrollHeight}px`;
            dragBarDomElement.addEventListener('mousedown', () => {
                columnBeingResized = dragBarDomElement.parentElement;
            });
            return dragBarDomElement;
        };

        headers.forEach((header, index) => {
            if ((index + 1 !== this.rowLength)) {
                header.append(dragBar(header));
            }
        });
        // Reset dragbar height on resize
        new ResizeObserver((event) => {
            const dragBars = headers.map(header => Array.from(header.children)).flat().filter(el => el.tagName == 'DRAG-BAR') as HTMLElement[];
            dragBars.forEach(dragBar => {
                dragBar.style.height = `${event[0].contentRect.height}px`;
            });
        }).observe(this.table.nativeElement);

    }

    setBorders(): void {
        if (!this.table) {
            return;
        }

        const allCells = Array.from(this.table.nativeElement.children).map(child => Array.from(child.children)).flat() as HTMLElement[];
        allCells.forEach((cell, index) => {
            // Borders Horizontal
            if (this.bordersH) {
                if (!(index + 1 > allCells.length - this.table.nativeElement.firstChild.childNodes.length)) {
                    cell.classList.add('≈border-b-2');
                }
            } else {
                cell.classList.remove('≈border-b-2');
            }
            // Borders Vertical
            if (this.bordersV) {
                if (!!cell.nextElementSibling) {
                    cell.classList.add('≈border-r-2');
                }
            } else {
                cell.classList.remove('≈border-r-2');
            }
        });

        if (this.outerBorders) {
            this.table.nativeElement.parentElement.classList.add('≈border-2', '≈rounded-md');
        } else {
            this.table.nativeElement.parentElement.classList.remove('≈border-2', '≈rounded-md');
        }
    }

    setColumnWidth() {
        // const headerRow = Array.from(this.table?.nativeElement?.firstElementChild.children) as HTMLElement[];
        // headerRow.forEach((header, index) => {
        //   header.style.width = `${this.table.nativeElement.getBoundingClientRect().width / this.rowLength}px`;
        // });
    }
}
